import React, { useState, useEffect } from "react";
import { httpService } from "../../services/httpService";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";
import { FaFileInvoice, FaBox, FaCalendarAlt, FaRupeeSign, FaTag, FaClock } from "react-icons/fa";

const PurchaseHistory = ({ productId, inventoryId }) => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (productId) {
      fetchPurchaseHistory();
    }
  }, [productId]);

  const fetchPurchaseHistory = async () => {
    setLoading(true);
    try {
      const response = await httpService.get(`/vendorproduct/product-purchase-history/${productId}`);
      if (response.code === 200) {
        setPurchases(response.data);
      }
    } catch (err) {
      console.error("Error fetching purchase history:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoaderSpiner text="Loading purchase history..." />;

  return (
    <div className="p-2 bg-white rounded-lg shadow">
      <h2 className="text-xl font-bold mb-4 text-gray-800">Purchase History</h2>
      
      {purchases.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <FaBox className="w-16 h-16 mx-auto" />
          </div>
          <p className="text-gray-500 text-lg">No purchase history found for this product.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <FaCalendarAlt className="w-3 h-3" />
                    Date
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <FaFileInvoice className="w-3 h-3" />
                    Invoice #
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Supplier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <FaRupeeSign className="w-3 h-3" />
                    Unit Price
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <FaRupeeSign className="w-3 h-3" />
                    Total
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <FaTag className="w-3 h-3" />
                    Batch #
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <FaClock className="w-3 h-3" />
                    Expiry Date
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {purchases.map((purchase, idx) => (
                <tr key={idx} className="hover:bg-gray-50 transition-colors duration-200">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(purchase.date).toLocaleDateString('en-IN')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                    {purchase.invoiceNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {purchase.supplierName || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
                      {purchase.quantity}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ₹{purchase.unitPrice?.toLocaleString() || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                    ₹{purchase.total?.toLocaleString() || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-xs font-mono">
                      {purchase.batchNumber || '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {purchase.expiryDate ? (
                      <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                        new Date(purchase.expiryDate) < new Date()
                          ? 'bg-red-100 text-red-700'
                          : new Date(purchase.expiryDate) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {new Date(purchase.expiryDate).toLocaleDateString('en-IN', {
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">N/A</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PurchaseHistory;